import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import { Modal, Input } from "antd";
import tokenListData from "../tokenList.json";

const nativeTokenSymbols = {
    1: 'ETH',
    1313161554: 'ETH',
    42161: 'ETH',
    43114: 'AVAX',
    8453: 'ETH',
    56: 'BNB',
    250: 'FTM',
    100: 'xDAI',
    8217: 'KLAY',
    10: 'ETH',
    137: 'MATIC',
    324: 'ETH',
};

function TokenListModal({ isOpen, onClose, onSelectToken, changeToken, signer, chainId, setSelectedTokenBalance }) {
    const [tokenList, setTokenList] = useState([]);
    const [defaultTokensSet, setDefaultTokensSet] = useState(false);
    const [balances, setBalances] = useState({});
    const [provider, setProvider] = useState(null);
    const [searchInput, setSearchInput] = useState("");

    useEffect(() => {
        async function fetchTokenList(chainId) {
            const tokenListByChain = tokenListData[chainId];
            if (tokenListByChain) {
                setTokenList(tokenListByChain);
            } else {
                console.error(`No tokens found for chain ID: ${chainId}`);
            }
        }

        async function init() {
            if (signer) {
                const network = await signer.provider.getNetwork();
                setProvider(signer.provider);
                fetchTokenList(chainId || network.chainId);
            }
        }

        init();
    }, [signer, chainId]);

    async function fetchTokenBalance(token, provider, signer, chainId, setBalances) {
        const address = await signer.getAddress();
        let balance;
        try {
            if (token.address === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
                // Native token balance
                balance = await provider.getBalance(address);
                balance = ethers.utils.formatEther(balance);
            } else {
                // Non-native token balance
                const tokenContract = new ethers.Contract(token.address, [
                    {
                        "constant": true,
                        "inputs": [{ "name": "_owner", "type": "address" }],
                        "name": "balanceOf",
                        "outputs": [{ "name": "balance", "type": "uint256" }],
                        "payable": false,
                        "type": "function"
                    },
                    {
                        "constant": true,
                        "inputs": [],
                        "name": "decimals",
                        "outputs": [{ "name": "", "type": "uint8" }],
                        "payable": false,
                        "type": "function"
                    }
                ], provider);
                balance = await tokenContract.balanceOf(address);
                const decimals = await tokenContract.decimals();
                balance = ethers.utils.formatUnits(balance, decimals);
            }
        } catch (error) {
            console.error(`Failed to fetch balance for ${token.name}:`, error);
            balance = 'Error';
        }
        setBalances(prevBalances => ({ ...prevBalances, [token.ticker]: balance }));
        return balance;
    }

    const fetchNativeBalance = async (initialToken) => {
        if (!provider || !signer || chainId === null) return;

        const address = await signer.getAddress();
        try {
            const balance = await provider.getBalance(address);
            const formattedBalance = ethers.utils.formatEther(balance);
            setBalances(prevBalances => ({
                ...prevBalances,
                [nativeTokenSymbols[chainId]]: formattedBalance
            }));
            if (initialToken) {
                setSelectedTokenBalance(formattedBalance);
            }
        } catch (error) {
            console.error(`Failed to fetch balance for native token (${nativeTokenSymbols[chainId]}):`, error);
        }
    };

    const fetchTokenBalances = async () => {
        if (!provider || !signer || chainId === null) return;

        const address = await signer.getAddress();
        for (let token of tokenList) {
            if (token.address === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') continue; // Skip native token

            let balance;
            try {
                const tokenContract = new ethers.Contract(token.address, [
                    {
                        "constant": true,
                        "inputs": [{ "name": "_owner", "type": "address" }],
                        "name": "balanceOf",
                        "outputs": [{ "name": "balance", "type": "uint256" }],
                        "payable": false,
                        "type": "function"
                    },
                    {
                        "constant": true,
                        "inputs": [],
                        "name": "decimals",
                        "outputs": [{ "name": "", "type": "uint8" }],
                        "payable": false,
                        "type": "function"
                    }
                ], provider);
                balance = await tokenContract.balanceOf(address);
                const decimals = await tokenContract.decimals();
                balance = ethers.utils.formatUnits(balance, decimals);
            } catch (error) {
                console.error(`Failed to fetch balance for ${token.name}:`, error);
                balance = 'Error';
            }
            setBalances(prevBalances => ({ ...prevBalances, [token.ticker]: balance }));
        }
    };

    useEffect(() => {
        if (tokenList.length && !defaultTokensSet) {
            const nativeToken = tokenList.find(token => token.address === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') || {
                address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
                ticker: nativeTokenSymbols[chainId],
                name: nativeTokenSymbols[chainId],
                img: 'https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png', // Fallback native token image URL
            };

            onSelectToken(nativeToken, 1);
            fetchNativeBalance(nativeToken);
            fetchTokenBalances();
            setDefaultTokensSet(true);
        }
    }, [tokenList, defaultTokensSet, onSelectToken, chainId, balances, setSelectedTokenBalance]);

    useEffect(() => {
        async function fetchTokenList(chainId) {
            const tokenListByChain = tokenListData[chainId];
            if (tokenListByChain) {
                setTokenList(tokenListByChain);
            } else {
                console.error(`No tokens found for chain ID: ${chainId}`);
            }
        }

        if (chainId) {
            setDefaultTokensSet(false);
            fetchTokenList(chainId);
        }
    }, [chainId]);

    function handleTokenSelect(token) {
        onSelectToken(token, changeToken);
        fetchTokenBalance(token, provider, signer, chainId, setBalances)
            .then(balance => {
                if (changeToken === 1) {
                    setSelectedTokenBalance(balance || '0');
                }
            });
        onClose();
    }

    function handleImageError(e) {
        e.target.onerror = null; // Prevents looping if the fallback image also fails
       
    }

    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const filteredTokenList = tokenList.filter(token =>
        token.name.toLowerCase().includes(searchInput.toLowerCase()) ||
        token.ticker.toLowerCase().includes(searchInput.toLowerCase())
    );

    return (
        <Modal open={isOpen} footer={null} onCancel={onClose} title="Select a token">
            <Input
                placeholder="Search token"
                value={searchInput}
                onChange={handleSearchInputChange}
                style={{
                      marginBottom: '20px',
                      maxHeight: '50px',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      display: 'block',
                      width: '90%' // Adjust the width as needed
                  }}
              />
            <div className="modalContent">
                {filteredTokenList.map((token, i) => (
                    <div className="tokenChoice" key={i} onClick={() => handleTokenSelect(token)}>
                        <img src={token.img} alt={token.ticker} className="tokenLogo" onError={handleImageError} />
                        <div className="tokenTicker" style={{ display: 'none' }}>{token.ticker}</div>
                        <div className="tokenChoiceNames">
                            <div className="tokenName">{token.name}</div>
                            <div className="tokenTicker">{token.ticker} {balances[token.ticker] !== undefined ? balances[token.ticker] : 'Loading...'}</div>
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
}

export default TokenListModal;
